<script setup lang="ts">
  import { onMounted, Ref, ref, useCssModule, watch } from 'vue';

  const classes = useCssModule();

  export interface VerificationCodeInputProps {
    digitCount?: number;
    /**
     * Error text to display.
     */
    errorText?: string;
    /**
     * Success text to display.
     */
    successText?: string;
    /**
     * Hint text to display.
     */
    hintText?: string;
    /**
     * One time password/code
     */
    modelValue?: string;
  }

  const props = withDefaults(defineProps<VerificationCodeInputProps>(), {
    digitCount: 6,
    errorText: undefined,
    successText: undefined,
    hintText: undefined,
    modelValue: undefined,
  });

  const emit = defineEmits<{
    (e: 'update:model-value', value: string | null): void;
    (e: 'change', params: { originalEvent: Event; value: string | null }): void;
    (e: 'focus', params: Event): void;
    (e: 'blur', params: Event): void;
  }>();

  const data: Ref<Array<string>> = ref([]);

  onMounted(() => {
    if (props.modelValue) {
      data.value = props.modelValue?.split('');
    }
  });

  function handleInput(event: InputEvent, index) {
    data.value[index] = event.target?.['value'];
    updateModel(event);

    if (event.inputType === 'deleteContentBackward') {
      moveToPrev(event);
    } else if (event.inputType === 'insertText' || event.inputType === 'deleteContentForward') {
      moveToNext(event);
    }
  }

  function updateModel(event: Event) {
    const newValue = data.value.join('');
    emit('update:model-value', newValue);
    emit('change', { originalEvent: event, value: newValue });
  }

  function moveToPrev(event: InputEvent | KeyboardEvent) {
    const prevInput: HTMLInputElement = findPrevInput(event.target as HTMLInputElement);

    if (prevInput) {
      prevInput.focus();
      prevInput.select();
    }
  }

  function moveToNext(event: InputEvent | KeyboardEvent) {
    const nextInput: HTMLInputElement = findNextInput(event.target as HTMLInputElement);

    if (nextInput) {
      nextInput.focus();
      nextInput.select();
    }
  }

  function findPrevInput(element: HTMLInputElement) {
    const prevElement = element.previousElementSibling;

    if (!prevElement) return;

    return prevElement.nodeName === 'INPUT' ? prevElement : findPrevInput(prevElement as HTMLInputElement);
  }

  function findNextInput(element: HTMLInputElement) {
    const nextElement = element.nextElementSibling;

    if (!nextElement) return;

    return nextElement.nodeName === 'INPUT' ? nextElement : findNextInput(nextElement as HTMLInputElement);
  }

  function handleFocus(event) {
    event.target.select();
    emit('focus', event);
  }

  function handleBlur(event: Event) {
    emit('blur', event);
  }

  function handleKeyDown(event: KeyboardEvent) {
    if (event.ctrlKey || event.metaKey) {
      return;
    }

    switch (event.code) {
      case 'ArrowLeft':
        moveToPrev(event);
        event.preventDefault();
        break;

      case 'ArrowUp':
      case 'ArrowDown':
        event.preventDefault();
        break;

      case 'Backspace':
        if (event.target?.['value'].length === 0) {
          moveToPrev(event);
          event.preventDefault();
        }
        break;

      case 'ArrowRight':
        moveToNext(event);
        event.preventDefault();

        break;

      case 'Enter':
      case 'NumpadEnter':
      case 'Tab':
        break;

      default:
        if (
          !(event.code !== 'Space' && Number(event.key) >= 0 && Number(event.key) <= 9) ||
          (data.value.join('').length >= props.digitCount && event.code !== 'Delete')
        ) {
          event.preventDefault();
        }

        break;
    }
  }

  function handlePaste(event: ClipboardEvent) {
    const paste = event.clipboardData?.getData('text');

    if (paste?.length) {
      const pastedCode = paste?.substring(0, props.digitCount);

      const parsed = parseInt(pastedCode);

      if (!isNaN(parsed)) {
        data.value = pastedCode.split('');
        updateModel(event);
      }
    }

    event.preventDefault();
  }

  watch(
    () => props.modelValue,
    (newValue) => {
      data.value = newValue ? newValue.split('') : new Array(props.digitCount);
    },
  );
</script>

<template>
  <div
    class="stash-confirmation-code-input inline-block"
    data-test="stash-confirmation-code-input"
    :class="[classes['stash-confirmation-code-input-container']]"
  >
    <div class="mb-3 flex gap-6">
      <template v-for="field in props.digitCount" :key="field">
        <input
          data-test="stash-confirmation-code-input__otp"
          class="border bg-white p-2 text-center text-base font-medium text-ice-900 focus:border-blue-500"
          :class="[
            classes['otp-code-input'],
            { [classes['has-error']]: !!props.errorText },
            { [classes['has-success']]: !!props.successText },
          ]"
          type="text"
          inputmode="numeric"
          maxlength="1"
          :value="data[field - 1]"
          pattern="[0-9]"
          autocomplete="one-time-code"
          @input="handleInput($event as InputEvent, field - 1)"
          @keydown="handleKeyDown"
          @paste="handlePaste"
          @focus="handleFocus"
          @blur="handleBlur"
        />
      </template>
    </div>
    <small
      v-if="props.errorText"
      class="mt-1 block whitespace-pre-line pr-6 text-center text-red-500"
      data-test="field-error"
    >
      {{ props.errorText }}
    </small>
    <small
      v-if="props.successText"
      class="mt-1 block whitespace-pre-line pr-6 text-center text-green-500"
      data-test="field-success"
    >
      {{ props.successText }}
    </small>
    <small
      v-if="props.hintText"
      class="mt-1 block whitespace-pre-line pr-6 text-center text-ice-700"
      data-test="field-hint"
    >
      {{ props.hintText }}
    </small>
  </div>
</template>

<style module>
  @layer utilities {
    .stash-confirmation-code-input-container {
      --otp-digits: v-bind(digitCount);
      --otp-container-width: calc(var(--otp-digits) * 60px);

      width: var(--otp-container-width);
    }

    .otp-code-input {
      border-radius: 4px;
      caret-color: transparent;
      height: 36px;
      width: 36px;
    }

    .otp-code-input.has-error:not(:focus) {
      border-color: var(--color-red-500);
    }

    .otp-code-input.has-success:not(:focus) {
      border-color: var(--color-green-500);
    }

    .otp-code-input:focus {
      box-shadow: 0 0 0 3px rgb(0 114 240 / 15%);
      outline: none;
    }
  }
</style>
